import React, { useState, useEffect, useCallback } from 'react';
import { WizardData, Voice, TTS_MODELS, SAMPLE_VOICES } from '../types';
import { useVoicePreview } from '../utils/voicePreview';

interface VoiceStepProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  apiKey: string;
}

const VoiceStep: React.FC<VoiceStepProps> = ({ 
  data, 
  updateData, 
  onNext, 
  onPrevious,
  apiKey
}) => {
  const [voices, setVoices] = useState<Voice[]>(SAMPLE_VOICES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const { playPreview } = useVoicePreview(apiKey);

  const fetchVoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('https://api.elevenlabs.io/v1/voices', {
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch voices: ${response.status}`);
      }

      const result = await response.json();
      setVoices(result.voices || SAMPLE_VOICES);
    } catch (err) {
      console.error('Error fetching voices:', err);
      setError('Unable to load voices from ElevenLabs. Using sample voices.');
      setVoices(SAMPLE_VOICES);
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  useEffect(() => {
    fetchVoices();
  }, [fetchVoices]);

  const availableFilters = [
    {
      key: 'gender',
      label: 'Gender',
      options: ['male', 'female', 'neutral']
    },
    {
      key: 'accent',
      label: 'Accent',
      options: ['american', 'british', 'australian', 'indian', 'african']
    },
    {
      key: 'age',
      label: 'Age',
      options: ['young', 'middle_aged', 'old']
    },
    {
      key: 'use_case',
      label: 'Use Case',
      options: ['narration', 'characters_animation', 'news', 'conversational']
    },
    {
      key: 'description',
      label: 'Style',
      options: ['calm', 'professional', 'friendly', 'authoritative', 'warm']
    }
  ];

  const toggleFilter = (filter: string) => {
    setSelectedFilters(prev =>
      prev.includes(filter)
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const filteredVoices = voices.filter(voice => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        voice.name?.toLowerCase().includes(query) ||
        voice.description?.toLowerCase().includes(query) ||
        voice.category?.toLowerCase().includes(query);
      
      if (!matchesSearch) return false;
    }

    // Tag filters
    if (selectedFilters.length > 0) {
      const voiceLabels = voice.labels || {};
      const voiceAccent = voiceLabels.accent?.toLowerCase();
      const voiceGender = voiceLabels.gender?.toLowerCase();
      const voiceAge = voiceLabels.age?.toLowerCase();
      const voiceUseCase = voiceLabels.use_case?.toLowerCase();
      const voiceDescription = voiceLabels.description?.toLowerCase();

      const matchesFilters = selectedFilters.every(filter => {
        const filterLower = filter.toLowerCase();
        return (
          voiceGender === filterLower ||
          voiceAccent === filterLower ||
          voiceAge === filterLower ||
          voiceUseCase === filterLower ||
          voiceDescription?.includes(filterLower)
        );
      });

      if (!matchesFilters) return false;
    }

    return true;
  });

  const handleModelSelect = (modelId: string) => {
    updateData({ ttsModel: modelId });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (data.voiceId && data.ttsModel) {
      onNext();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Voice Selection Section */}
      <div className="space-y-6">
        <h4 className="text-xl font-semibold text-white flex items-center">
          <span className="mr-2">🎤</span> Select Voice
        </h4>

        {/* Search and Filter */}
        <div className="space-y-3">
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Search voices by name, description, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className={`px-4 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white hover:bg-gray-700 transition-colors ${
                selectedFilters.length > 0 ? 'border-blue-500' : ''
              }`}
            >
              <span className="mr-2">🔧</span>
              Filters {selectedFilters.length > 0 && `(${selectedFilters.length})`}
            </button>
          </div>

          {/* Collapsible Filters */}
          {filtersExpanded && (
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-600 space-y-3">
              {availableFilters.map(filterGroup => (
                <div key={filterGroup.key}>
                  <p className="text-xs text-gray-400 mb-2">{filterGroup.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {filterGroup.options.map(option => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => toggleFilter(option)}
                        className={`px-3 py-1 rounded-lg text-xs transition-colors ${
                          selectedFilters.includes(option)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {option.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {selectedFilters.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedFilters([])}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Loading and Error States */}
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto"></div>
            <p className="text-gray-400 mt-2">Loading voices...</p>
          </div>
        )}

        {error && (
          <div className="bg-yellow-900/20 rounded-xl p-4 border border-yellow-600">
            <p className="text-yellow-400 text-sm">{error}</p>
          </div>
        )}

        {/* Voice Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {filteredVoices.map((voice) => (
              <div
                key={voice.voice_id}
                onClick={() => updateData({ voiceId: voice.voice_id })}
                className={`
                  bg-gray-800 rounded-xl p-4 cursor-pointer transition-all border
                  ${data.voiceId === voice.voice_id
                    ? 'border-blue-500 bg-blue-600/20'
                    : 'border-gray-600 hover:border-blue-400 hover:bg-gray-700'
                  }
                `}
              >
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-white truncate">{voice.name}</h5>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      playPreview(voice.voice_id);
                    }}
                    className="text-blue-400 hover:text-blue-300 text-sm p-1"
                    title="Play preview"
                  >
                    ▶️
                  </button>
                </div>
                {voice.description && (
                  <p className="text-xs text-gray-400 line-clamp-2">{voice.description}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-1">
                  {voice.labels?.gender && (
                    <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">
                      {voice.labels.gender}
                    </span>
                  )}
                  {voice.labels?.accent && (
                    <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">
                      {voice.labels.accent}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredVoices.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">No voices found matching your criteria</p>
          </div>
        )}
      </div>

      {/* TTS Model Selection */}
      <div className="space-y-6">
        <h4 className="text-xl font-semibold text-white flex items-center">
          <span className="mr-2">⚡</span> Text-to-Speech Model
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TTS_MODELS.map((model) => (
            <div
              key={model.id}
              onClick={() => handleModelSelect(model.id)}
              className={`
                bg-gray-800 rounded-lg p-3 cursor-pointer transition-all border
                ${data.ttsModel === model.id
                  ? 'border-blue-500 bg-blue-600/20'
                  : 'border-gray-600 hover:border-blue-400 hover:bg-gray-700'
                }
              `}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h6 className="font-semibold text-white flex items-center text-sm">
                    <span className="mr-2">{model.icon}</span>
                    {model.name}
                  </h6>
                  <p className="text-xs text-gray-400 mt-1">{model.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onPrevious}
          className="bg-gray-700 hover:bg-gray-600 text-white font-medium px-6 py-3 rounded-xl transition-colors"
        >
          ← Previous
        </button>
        <button
          type="submit"
          disabled={!data.voiceId || !data.ttsModel}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
        >
          Next Step →
        </button>
      </div>
    </form>
  );
};

export default VoiceStep;